import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';

export default function ThemeProvider({ children }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      // Load theme settings from /api/theme
      const res = await fetch(`${API}/theme`);
      const { success, data } = await res.json();
      if (success && data) {
        applyTheme(data);
      }
      // Also load custom theme overrides from settings
      const settingsRes = await fetch(`${API}/settings`);
      const settingsData = await settingsRes.json();
      const arr = settingsData.data || settingsData || [];
      if (Array.isArray(arr)) {
        const themeSettings = {};
        arr.filter(s => s.group === 'theme').forEach(s => { themeSettings[s.key] = s.value; });
        if (Object.keys(themeSettings).length > 0) {
          applyCustomTheme(themeSettings);
        }
      }
    } catch (err) {
      console.warn('ThemeProvider: Could not load theme settings', err);
    } finally {
      setLoaded(true);
    }
  };

  const applyCustomTheme = (t) => {
    const root = document.documentElement;
    if (t.theme_primary_color) {
      root.style.setProperty('--color-primary', t.theme_primary_color);
      root.style.setProperty('--color-primary-light', lightenColor(t.theme_primary_color, 15));
      root.style.setProperty('--color-primary-dark', darkenColor(t.theme_primary_color, 10));
      root.style.setProperty('--color-primary-glow', hexToRgba(t.theme_primary_color, 0.3));
      root.style.setProperty('--color-primary-subtle', hexToRgba(t.theme_primary_color, 0.08));
      root.style.setProperty('--shadow-glow', `0 0 30px ${hexToRgba(t.theme_primary_color, 0.15)}`);
      root.style.setProperty('--color-border-primary', hexToRgba(t.theme_primary_color, 0.3));
    }
    if (t.theme_secondary_color) root.style.setProperty('--color-secondary', t.theme_secondary_color);
    if (t.theme_bg_color) root.style.setProperty('--color-bg-dark', t.theme_bg_color);
    if (t.theme_card_bg_color) root.style.setProperty('--color-bg-card', t.theme_card_bg_color);
    if (t.theme_text_color) root.style.setProperty('--color-text-primary', t.theme_text_color);
    if (t.theme_font_family) root.style.setProperty('--font-primary', t.theme_font_family);
    if (t.theme_border_radius) root.style.setProperty('--radius-md', `${t.theme_border_radius}px`);
  };

  const applyTheme = (t) => {
    const root = document.documentElement;

    // Colors
    root.style.setProperty('--color-primary', t.primaryColor);
    root.style.setProperty('--color-primary-light', lightenColor(t.primaryColor, 15));
    root.style.setProperty('--color-primary-dark', darkenColor(t.primaryColor, 10));
    root.style.setProperty('--color-primary-glow', hexToRgba(t.primaryColor, 0.3));
    root.style.setProperty('--color-primary-subtle', hexToRgba(t.primaryColor, 0.08));
    root.style.setProperty('--color-secondary', t.secondaryColor);
    root.style.setProperty('--color-bg-dark', t.backgroundColor);
    root.style.setProperty('--color-bg-card', t.surfaceColor);
    root.style.setProperty('--color-text-primary', t.textColor);
    root.style.setProperty('--color-text-secondary', t.textSecondary);

    // Typography
    root.style.setProperty('--font-primary', t.fontFamily);
    root.style.setProperty('--h1-size', `${t.h1Size}px`);
    root.style.setProperty('--h2-size', `${t.h2Size}px`);
    root.style.setProperty('--h3-size', `${t.h3Size}px`);
    root.style.setProperty('--body-size', `${t.bodySize}px`);

    // Layout
    root.style.setProperty('--container-max', `${t.containerWidth}px`);
    root.style.setProperty('--navbar-height', `${t.navbarHeight}px`);
    root.style.setProperty('--logo-size', `${t.logoSize}px`);
    root.style.setProperty('--radius-md', `${t.borderRadius}px`);
    root.style.setProperty('--card-padding', `${t.cardPadding}px`);
    root.style.setProperty('--section-padding', `${t.sectionPadding}px`);
    root.style.setProperty('--client-logo-width', `${t.clientLogoWidth || 150}px`);
    root.style.setProperty('--client-logo-height', `${t.clientLogoHeight || 70}px`);

    // Shadow glow based on primary
    root.style.setProperty('--shadow-glow', `0 0 30px ${hexToRgba(t.primaryColor, 0.15)}`);
    root.style.setProperty('--color-border-primary', hexToRgba(t.primaryColor, 0.3));

    // Custom CSS injection
    if (t.customCSS) {
      let styleEl = document.getElementById('theme-custom-css');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'theme-custom-css';
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = t.customCSS;
    }
  };

  return children;
}

// --- Utility helpers ---

function hexToRgba(hex, alpha) {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return hex;
  }
}

function lightenColor(hex, percent) {
  try {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
    g = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
    b = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch {
    return hex;
  }
}

function darkenColor(hex, percent) {
  try {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.max(0, Math.round(r * (1 - percent / 100)));
    g = Math.max(0, Math.round(g * (1 - percent / 100)));
    b = Math.max(0, Math.round(b * (1 - percent / 100)));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch {
    return hex;
  }
}
